import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import StringField from '../../core/components/Fields/StringField.tsx';
import TextField from '../../core/components/Fields/TextField.tsx';
import Button from '../../core/components/Button.tsx';
import { WorkistLogo } from '../../core/components/WorkistLogo.tsx';
import { fetchFormRequestRenderData, FormRequestRenderData, submitFormRequest } from '../api.ts';

import FormNotAvailablePage from './FormNotAvailablePage.tsx';
import { LabeledRow, LanguageSwitcher } from './PublicFormPage.tsx';

const RequestReceivedPage = ({ tenantName }: { tenantName?: string }) => {
    const { t } = useTranslation('forms');
    return (
        <div className="h-full bg-surface-medium text-content-strong flex items-center justify-center overflow-y-auto">
            <div className="max-w-md text-center p-10 bg-surface-weak border border-solid border-line-weak rounded">
                <h1 className="text-2xl font-semibold mb-3">{t('request.received.title')}</h1>
                <p className="text-content-medium mb-2">
                    {tenantName
                        ? t('request.received.bodyWithTenant', { tenant: tenantName })
                        : t('request.received.body')}
                </p>
                <p className="text-content-medium mb-6">{t('request.received.hint')}</p>
                <div className="text-xs text-content-medium flex items-center gap-2 justify-center">
                    <span>{t('poweredBy')}</span>
                    <a href="https://workist.com" target="_blank" rel="noopener noreferrer">
                        <WorkistLogo className="h-4 w-auto" />
                    </a>
                </div>
            </div>
        </div>
    );
};

const PublicFormRequestPage = () => {
    const { token = '' } = useParams();
    const { t, i18n } = useTranslation('forms');

    const [shape, setShape] = useState<FormRequestRenderData | null>(null);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [logoFailed, setLogoFailed] = useState(false);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [note, setNote] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        fetchFormRequestRenderData(token, controller.signal)
            .then((data) => setShape(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setLoadError(err);
            });
        return () => controller.abort();
    }, [token]);

    if (loadError) return <FormNotAvailablePage />;
    if (!shape) {
        return (
            <div className="w-full h-screen flex items-center justify-center text-content-medium">{t('loading')}</div>
        );
    }
    if (submitted) return <RequestReceivedPage tenantName={shape.tenant.name} />;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        if (!email.trim()) {
            setSubmitError(t('errors.missingEmail'));
            return;
        }
        setSubmitting(true);
        try {
            await submitFormRequest(token, {
                email: email.trim(),
                phone: phone.trim(),
                note: note.trim(),
                language: i18n.language?.split('-')[0] || 'en',
            });
            setSubmitted(true);
        } catch (err) {
            const code = axios.isAxiosError(err) ? err.response?.data?.error : null;
            setSubmitError(
                code === 'invalid_email' || code === 'missing_email'
                    ? t('request.errors.invalidEmail')
                    : t('request.errors.submitFailed')
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-surface-medium text-content-strong">
            <header className="flex-none border-b border-solid border-line-weak bg-surface-weak">
                <div className="max-w-3xl mx-auto flex items-center justify-between py-4 px-6">
                    {shape.tenant.logo_url && !logoFailed ? (
                        <img
                            src={shape.tenant.logo_url}
                            alt={shape.tenant.name}
                            className="h-8 w-auto object-contain"
                            onError={() => setLogoFailed(true)}
                        />
                    ) : (
                        <div className="font-semibold">{shape.tenant.name || shape.channel.name}</div>
                    )}
                    <LanguageSwitcher />
                </div>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto">
                <main className="max-w-3xl mx-auto px-6 py-10">
                    <h1 className="text-2xl font-semibold mb-2">
                        {t('request.heading', { channel: shape.channel.name })}
                    </h1>
                    <p className="text-content-medium mb-6">{shape.form.intro_text || t('request.description')}</p>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                        <section className="bg-surface-weak border border-solid border-line-weak rounded p-6 flex flex-col gap-3">
                            <LabeledRow label={t('fields.yourEmail')} required>
                                <div className="flex flex-col gap-1">
                                    <StringField
                                        value={email}
                                        onValueChange={(v: any) => setEmail(String(v ?? ''))}
                                        type="email"
                                        required
                                    />
                                    <span className="text-xs text-content-medium">{t('request.emailHelp')}</span>
                                </div>
                            </LabeledRow>
                            <LabeledRow label={t('request.phone')}>
                                <StringField
                                    value={phone}
                                    onValueChange={(v: any) => setPhone(String(v ?? ''))}
                                    type="tel"
                                />
                            </LabeledRow>
                            <LabeledRow label={t('request.note')}>
                                <TextField value={note} onValueChange={(v: any) => setNote(String(v ?? ''))} />
                            </LabeledRow>
                        </section>

                        {submitError && <div className="text-content-danger-weak text-sm">{submitError}</div>}

                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                variant="primary"
                                className="w-full px-6 sm:w-auto"
                                disabled={submitting}
                            >
                                {t('request.submit')}
                            </Button>
                        </div>
                    </form>
                </main>

                <footer className="max-w-3xl mx-auto px-6 py-8 text-content-medium text-xs flex items-center gap-2 justify-center">
                    <span>{t('poweredBy')}</span>
                    <a href="https://workist.com" target="_blank" rel="noopener noreferrer">
                        <WorkistLogo className="h-4 w-auto" />
                    </a>
                </footer>
            </div>
        </div>
    );
};

export default PublicFormRequestPage;
